import { useEffect, useState } from 'react';

const FB_TRACKING_KEY = 'fb_tracking_data';

interface FacebookTrackingData {
  fbc: string | null;
  fbp: string | null;
  fbclid: string | null;
  capturedAt: number;
}

// Generate a browser ID similar to fbp format: fb.1.{timestamp}.{random}
const generateBrowserId = (): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 12) + Math.random().toString(36).substring(2, 12);
  return `fb.1.${timestamp}.${random}`;
};

// Construct fbc from fbclid: fb.1.{timestamp}.{fbclid}
const constructFbc = (fbclid: string): string => {
  const timestamp = Date.now();
  return `fb.1.${timestamp}.${fbclid}`;
};

// Get stored tracking data from localStorage
const getStoredTracking = (): FacebookTrackingData | null => {
  try {
    const stored = localStorage.getItem(FB_TRACKING_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Failed to read Facebook tracking from localStorage:', e);
  }
  return null;
};

// Save tracking data to localStorage
const saveTracking = (data: FacebookTrackingData): void => {
  try {
    localStorage.setItem(FB_TRACKING_KEY, JSON.stringify(data));
    console.log('Facebook tracking data saved to localStorage:', data);
  } catch (e) {
    console.warn('Failed to save Facebook tracking to localStorage:', e);
  }
};

// Try to get cookies (may fail due to blockers)
const getCookies = (): { fbc: string | null; fbp: string | null } => {
  try {
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [key, value] = cookie.trim().split('=');
      if (key && value) {
        acc[key.trim()] = value.trim();
      }
      return acc;
    }, {} as Record<string, string>);
    
    return {
      fbc: cookies['_fbc'] || null,
      fbp: cookies['_fbp'] || null,
    };
  } catch (e) {
    return { fbc: null, fbp: null };
  }
};

// Initialize and capture Facebook tracking data
export const initializeFacebookTracking = (): FacebookTrackingData => {
  const urlParams = new URLSearchParams(window.location.search);
  const urlFbclid = urlParams.get('fbclid');
  const cookies = getCookies();
  const stored = getStoredTracking();
  
  // Start with stored data or empty
  let data: FacebookTrackingData = stored || {
    fbc: null,
    fbp: null,
    fbclid: null,
    capturedAt: Date.now(),
  };
  
  // If we have a fresh fbclid in URL, always update fbc
  if (urlFbclid) {
    data.fbclid = urlFbclid;
    data.fbc = constructFbc(urlFbclid);
    data.capturedAt = Date.now();
    console.log('Captured fbclid from URL:', urlFbclid);
  } else if (!data.fbc && cookies.fbc) {
    // Fall back to cookie if available
    data.fbc = cookies.fbc;
  }
  
  // For fbp: prefer cookie, then stored, then generate new
  if (cookies.fbp) {
    data.fbp = cookies.fbp;
  } else if (!data.fbp) {
    // Generate our own browser ID as fallback
    data.fbp = generateBrowserId();
    console.log('Generated first-party fbp:', data.fbp);
  }
  
  // Save the updated data
  saveTracking(data);
  
  return data;
};

// Get current tracking data (from storage, with cookie fallback)
export const getFacebookTracking = (): { fbc: string | null; fbp: string | null } => {
  const stored = getStoredTracking();
  const cookies = getCookies();
  
  return {
    fbc: stored?.fbc || cookies.fbc || null,
    fbp: stored?.fbp || cookies.fbp || null,
  };
};

// React hook for Facebook tracking
export const useFacebookTracking = () => {
  const [tracking, setTracking] = useState<{ fbc: string | null; fbp: string | null }>({
    fbc: null,
    fbp: null,
  });
  
  useEffect(() => {
    // Initialize tracking on mount
    const data = initializeFacebookTracking();
    setTracking({ fbc: data.fbc, fbp: data.fbp });
    
    // Also listen for URL changes (SPA navigation)
    const handlePopState = () => {
      const newData = initializeFacebookTracking();
      setTracking({ fbc: newData.fbc, fbp: newData.fbp });
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  
  return tracking;
};

export default useFacebookTracking;
